/**
 * FEA-029: Terminal Settings Hook
 *
 * Manages terminal settings with localStorage persistence
 */

import { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';

export interface TerminalTheme {
  name: string;
  background: string;
  foreground: string;
  cursor: string;
  cursorAccent: string;
  selectionBackground: string;
  black: string;
  red: string;
  green: string;
  yellow: string;
  blue: string;
  magenta: string;
  cyan: string;
  white: string;
  brightBlack: string;
  brightRed: string;
  brightGreen: string;
  brightYellow: string;
  brightBlue: string;
  brightMagenta: string;
  brightCyan: string;
  brightWhite: string;
}

export interface TerminalSettings {
  // Appearance
  theme: string;
  fontSize: number;
  fontFamily: string;
  lineHeight: number;
  opacity: number;
  backgroundBlur: boolean;

  // Cursor
  cursorStyle: 'bar' | 'block' | 'underline';
  cursorBlink: boolean;

  // Behavior
  scrollback: number;
  copyOnSelect: boolean;
  rightClickPaste: boolean;
  bellSound: boolean;
  linkDetection: boolean;

  // Panel
  defaultState: 'docked' | 'floating' | 'minimized';
  dockPosition: 'bottom-right' | 'bottom-left' | 'right-side';
  rememberPosition: boolean;
  snapToEdges: boolean;
  floatingOpacity: number;

  // Startup
  autoStartCommand: string;
  workingDirectory: string;
  shell: string;

  // Advanced
  webglRenderer: boolean;
  fastScroll: boolean;

  // Context Selection
  contextHighlightColor: string;
  contextAutoExit: boolean;
  contextMultiSelect: boolean;
  contextFormat: 'compact' | 'detailed' | 'json';
  contextIncludeIds: boolean;
  contextAutoNewline: boolean;
  navigationContext: boolean; // Send context when user navigates to different pages

  // Window position (for floating mode)
  floatingX: number;
  floatingY: number;
  floatingWidth: number;
  floatingHeight: number;
}

const DEFAULT_SETTINGS: TerminalSettings = {
  // Appearance
  theme: 'tokyo-night',
  fontSize: 13,
  fontFamily: 'JetBrains Mono',
  lineHeight: 1,
  opacity: 100,
  backgroundBlur: false,

  // Cursor
  cursorStyle: 'bar',
  cursorBlink: true,

  // Behavior
  scrollback: 10000,
  copyOnSelect: false,
  rightClickPaste: true,
  bellSound: false,
  linkDetection: true,

  // Panel
  defaultState: 'docked',
  dockPosition: 'bottom-right',
  rememberPosition: true,
  snapToEdges: true,
  floatingOpacity: 95,

  // Startup
  autoStartCommand: 'claude',
  workingDirectory: '~/PROJECTS/FMEASTUDIO',
  shell: 'bash',

  // Advanced
  webglRenderer: true,
  fastScroll: true,

  // Context Selection
  contextHighlightColor: '#3b82f6',
  contextAutoExit: true,
  contextMultiSelect: false,
  contextFormat: 'compact',
  contextIncludeIds: false,
  contextAutoNewline: true,
  navigationContext: true, // Enabled by default

  // Window position
  floatingX: 100,
  floatingY: 100,
  floatingWidth: 600,
  floatingHeight: 400,
};

const STORAGE_KEY = 'fmea-terminal-settings';

export function useTerminalSettings() {
  const [settings, setSettings] = useState<TerminalSettings>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (e) {
      logger.error('[Terminal] Failed to load settings:', e);
    }
    return DEFAULT_SETTINGS;
  });

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      logger.error('[Terminal] Failed to save settings:', e);
    }
  }, [settings]);

  const updateSetting = useCallback(<K extends keyof TerminalSettings>(
    key: K,
    value: TerminalSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateSettings = useCallback((updates: Partial<TerminalSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return {
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    DEFAULT_SETTINGS,
  };
}
